// Что можно выбрать в форме лайфхака: категория и страна.
//
// Категории — короткий закрытый список, тот же, что на сервере
// (server/lifehacks/routes.mjs). Их пять, потому что столько помещается в ряд
// кнопок на телефоне, и потому что это те же вещи, из которых собирается сама
// поездка: перелёт, жильё, машина, еда, багаж. Шестая категория «разное»
// намеренно не заведена — в неё уезжает всё, и фильтр перестаёт работать.
//
// Страны — коды ISO 3166-1 alpha-2 и ничего больше. Названия на пяти языках
// сюда не переносятся: их даёт `Intl.DisplayNames`, который есть в браузере и
// знает все страны на всех языках интерфейса. Держать свой список названий
// значило бы переводить двести пятьдесят строк пять раз и потом следить за
// переименованиями.

export const CATEGORIES = ['flight', 'lodging', 'car', 'food', 'luggage'] as const;
export type Category = (typeof CATEGORIES)[number];

/** Те же пределы, что проверяет сервер: форма обязана отказать до отправки. */
export const MIN_TEXT = 10;
export const MAX_TEXT = 600;

export function isCategory(value: string): value is Category {
  return (CATEGORIES as readonly string[]).includes(value);
}

/**
 * Коды стран. Одной строкой, а не массивом из двухсот пятидесяти строк с
 * кавычками: так это читается как данные, а не как код, и правка не тонет в
 * синтаксисе.
 */
const CODES =
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS ' +
  'BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE ' +
  'EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM ' +
  'HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC ' +
  'LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA ' +
  'NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW ' +
  'SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO ' +
  'TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW';

export const COUNTRIES: readonly string[] = CODES.split(' ');

const COUNTRY_SET = new Set(COUNTRIES);

export function isCountry(value: string): boolean {
  return COUNTRY_SET.has(value);
}

/**
 * Название страны на языке интерфейса.
 *
 * `fallback: 'code'` вместо пустой строки: незнакомый браузеру код лучше
 * показать кодом, чем оставить пустую строку в списке, по которой не понять,
 * что вообще выбрано. Такие коды приходят из базы — там могло сохраниться
 * что-то, чего этот браузер ещё не знает.
 */
export function countryNamer(lang: string): (code: string) => string {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([lang], { type: 'region', fallback: 'code' });
  } catch {
    names = null;
  }
  return (code) => {
    if (!code) return '';
    try {
      return names?.of(code) ?? code;
    } catch {
      return code;
    }
  };
}

/** Страны, отсортированные по названию на языке читателя, а не по коду. */
export function sortedCountries(lang: string): { code: string; name: string }[] {
  const name = countryNamer(lang);
  return COUNTRIES.map((code) => ({ code, name: name(code) })).sort((a, b) =>
    a.name.localeCompare(b.name, lang),
  );
}
